import React, { useState } from 'react';
import { X, Download, AlertTriangle, CheckCircle, Clock, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../../catalyst/button';
import jsPDF from 'jspdf';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';

// Helper to infer category from job data
// FT-189: Use getJobDisplayName for canonical name
const inferCategoryFromJob = (job) => {
  const itemLower = getJobDisplayName(job).toLowerCase();
  const categoryLower = (job.category || '').toLowerCase();

  // Check category field first
  if (categoryLower.includes('ffe') || categoryLower.includes('fire')) return 'ffe';
  if (categoryLower.includes('lsa') || categoryLower.includes('life')) return 'lsa';
  if (categoryLower.includes('nav')) return 'nav';
  if (categoryLower.includes('gmdss') || categoryLower.includes('radio')) return 'gmdss';
  if (categoryLower.includes('medical')) return 'medical';
  if (categoryLower.includes('machinery')) return 'machinery';
  if (categoryLower.includes('electrical')) return 'electrical';
  if (categoryLower.includes('environmental')) return 'environmental';
  if (categoryLower.includes('operations')) return 'operations';

  // Fallback to item name
  if (itemLower.includes('fire') || itemLower.includes('extinguisher') || itemLower.includes('hose')) return 'ffe';
  if (itemLower.includes('lifeboat') || itemLower.includes('raft') || itemLower.includes('lifejacket') || itemLower.includes('lifebuoy')) return 'lsa';
  if (itemLower.includes('radar') || itemLower.includes('gps') || itemLower.includes('compass') || itemLower.includes('chart') || itemLower.includes('echo')) return 'nav';
  if (itemLower.includes('epirb') || itemLower.includes('sart') || itemLower.includes('vhf') || itemLower.includes('radio')) return 'gmdss';
  if (itemLower.includes('medical') || itemLower.includes('first aid')) return 'medical';
  if (itemLower.includes('engine') || itemLower.includes('pump') || itemLower.includes('machinery') || itemLower.includes('bilge') || itemLower.includes('steering')) return 'machinery';
  if (itemLower.includes('electrical') || itemLower.includes('battery') || itemLower.includes('generator') || itemLower.includes('lighting')) return 'electrical';
  if (itemLower.includes('sewage') || itemLower.includes('waste') || itemLower.includes('separator') || itemLower.includes('marpol')) return 'environmental';
  if (itemLower.includes('anchor') || itemLower.includes('mooring') || itemLower.includes('deck') || itemLower.includes('crane') || itemLower.includes('lifting')) return 'operations';

  return 'other';
};

// Helper to categorize jobs by domain
const categorizeJobs = (jobs) => {
  const categories = {
    ffe: { name: 'Fire Fighting Equipment', icon: '🔥', jobs: [] },
    lsa: { name: 'Life Saving Appliances', icon: '🛟', jobs: [] },
    nav: { name: 'Navigation Equipment', icon: '🧭', jobs: [] },
    gmdss: { name: 'GMDSS Equipment', icon: '📡', jobs: [] },
    medical: { name: 'Medical Equipment', icon: '⚕️', jobs: [] },
    machinery: { name: 'Machinery', icon: '⚙️', jobs: [] },
    electrical: { name: 'Electrical Systems', icon: '⚡', jobs: [] },
    environmental: { name: 'Environmental Systems', icon: '🌊', jobs: [] },
    operations: { name: 'Operations Equipment', icon: '🚢', jobs: [] },
    other: { name: 'Other', icon: '📋', jobs: [] }
  };

  jobs.forEach(job => {
    const categoryKey = inferCategoryFromJob(job);
    if (categories[categoryKey]) {
      categories[categoryKey].jobs.push(job);
    } else {
      categories.other.jobs.push(job);
    }
  });

  // Return only categories with jobs
  return Object.entries(categories)
    .filter(([key, cat]) => cat.jobs.length > 0)
    .map(([key, cat]) => ({ key, ...cat }));
};

// Collapsible category section component
const CategorySection = ({ category, statusColor = 'yellow' }) => {
  const [expanded, setExpanded] = useState(false);

  const colorClasses = {
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600' },
    red: { bg: 'bg-red-50', text: 'text-red-600' }
  };

  const colors = colorClasses[statusColor] || colorClasses.yellow;

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{category.icon}</span>
          <div className="text-left">
            <h5 className="text-sm font-medium text-gray-900">{category.name}</h5>
            <p className="text-xs text-gray-500">{category.jobs.length} items</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className={`border-t border-gray-200 p-3 ${colors.bg}`}>
          <ul className="space-y-2">
            {category.jobs.map(job => (
              <li key={job.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-900">{getJobDisplayName(job)}</span>
                <span className={`font-medium ${colors.text}`}>
                  {job.daysUntilDue < 0
                    ? `${Math.abs(job.daysUntilDue)} days overdue`
                    : `Due in ${job.daysUntilDue} days`
                  }
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MaintenanceReportModal = ({ isOpen, onClose, maintenanceJobs, maintenanceHistory }) => {
  if (!isOpen) return null;

  // Categorize jobs
  const overdueJobs = maintenanceJobs.filter(job => job.status === 'overdue');
  const dueSoonJobs = maintenanceJobs.filter(job => job.status === 'due_soon');
  const compliantJobs = maintenanceJobs.filter(job => job.status === 'compliant');
  
  // Get recent completions (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const recentCompletions = Object.entries(maintenanceHistory)
    .filter(([key, date]) => new Date(date) >= thirtyDaysAgo)
    .map(([key]) => {
      const job = maintenanceJobs.find(j => j.id === key);
      return { key, date: maintenanceHistory[key], job };
    })
    .filter(item => item.job)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.text('Maintenance Report', 20, yPos);
    yPos += 10;

    // Date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 20, yPos);
    yPos += 15;

    // Overdue Items
    if (overdueJobs.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(220, 38, 38); // Red
      doc.text('OVERDUE MAINTENANCE', 20, yPos);
      doc.setTextColor(0);
      yPos += 8;

      doc.setFontSize(10);
      overdueJobs.forEach(job => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(`• ${getJobDisplayName(job)} - ${Math.abs(job.daysUntilDue)} days overdue`, 25, yPos);
        yPos += 6;
      });
      yPos += 5;
    }

    // Due Soon Items
    if (dueSoonJobs.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(245, 158, 11); // Yellow
      doc.text('DUE SOON (Next 30 days)', 20, yPos);
      doc.setTextColor(0);
      yPos += 8;

      doc.setFontSize(10);
      dueSoonJobs.forEach(job => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(`• ${getJobDisplayName(job)} - Due in ${job.daysUntilDue} days`, 25, yPos);
        yPos += 6;
      });
      yPos += 5;
    }

    // Recent Completions
    if (recentCompletions.length > 0) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setTextColor(34, 197, 94); // Green
      doc.text('RECENTLY COMPLETED (Last 30 days)', 20, yPos);
      doc.setTextColor(0);
      yPos += 8;

      doc.setFontSize(10);
      recentCompletions.slice(0, 10).forEach(({ job, date }) => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(`• ${getJobDisplayName(job)} - ${new Date(date).toLocaleDateString('en-GB')}`, 25, yPos);
        yPos += 6;
      });
    }

    // Summary
    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
    }
    yPos += 10;
    doc.setFontSize(12);
    doc.text('SUMMARY', 20, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.text(`Total Items: ${maintenanceJobs.length}`, 25, yPos);
    yPos += 6;
    doc.text(`Overdue: ${overdueJobs.length}`, 25, yPos);
    yPos += 6;
    doc.text(`Due Soon: ${dueSoonJobs.length}`, 25, yPos);
    yPos += 6;
    doc.text(`Compliant: ${compliantJobs.length}`, 25, yPos);

    // Save
    doc.save(`maintenance-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Maintenance Report</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{maintenanceJobs.length}</p>
                    <p className="text-sm text-gray-500">Total Items</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-red-900">{overdueJobs.length}</p>
                    <p className="text-sm text-red-600">Overdue</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-yellow-400 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-yellow-900">{dueSoonJobs.length}</p>
                    <p className="text-sm text-yellow-600">Due Soon</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                  <div>
                    <p className="text-2xl font-bold text-green-900">{compliantJobs.length}</p>
                    <p className="text-sm text-green-600">Compliant</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Overdue Items - Grouped by Category */}
            {overdueJobs.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-medium text-red-900">Overdue Maintenance</h4>
                  <span className="text-sm text-gray-500">{overdueJobs.length} items</span>
                </div>
                <div className="space-y-2">
                  {categorizeJobs(overdueJobs).map(category => (
                    <CategorySection
                      key={category.key}
                      category={category}
                      statusColor="red"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Due Soon Items - Grouped by Category */}
            {dueSoonJobs.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-medium text-yellow-900">Due Soon (Next 30 days)</h4>
                  <span className="text-sm text-gray-500">{dueSoonJobs.length} items</span>
                </div>
                <div className="space-y-2">
                  {categorizeJobs(dueSoonJobs).map(category => (
                    <CategorySection
                      key={category.key}
                      category={category}
                      statusColor="yellow"
                    />
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <button
                    onClick={onClose}
                    className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    View Full Maintenance Schedule
                  </button>
                </div>
              </div>
            )}

            {/* Recent Completions */}
            {recentCompletions.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-medium text-green-900 mb-3">Recently Completed</h4>
                <div className="bg-green-50 rounded-lg p-4">
                  <ul className="space-y-2">
                    {recentCompletions.slice(0, 10).map(({ job, date }) => (
                      <li key={job.id} className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">{getJobDisplayName(job)}</span>
                        <span className="text-sm text-gray-600">
                          {new Date(date).toLocaleDateString('en-GB')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </Button>
            <Button
              onClick={generatePDF}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
            >
              <Download className="h-4 w-4 mr-2 inline" />
              Export PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceReportModal;